import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { StatusBadge } from '../common/StatusBadge';
import { Logo } from '../common/Logo';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { UserRole, UserStatus } from '../../types/auth';
import { contractService, Contract } from '../../services/contractService';
import { api } from '../../services/api';

interface NavigationItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    path: string;
    badge?: string;
    disabled?: boolean;
}

export const ClientLayout: React.FC = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation(['nav', 'common']);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);
    const [hasSignedContract, setHasSignedContract] = useState(false);
    const [contractsLoading, setContractsLoading] = useState(true);
    const [companyFrom1C, setCompanyFrom1C] = useState(false);
    const [hasBankingDetails, setHasBankingDetails] = useState(false);
    const [userFrom1C, setUserFrom1C] = useState(false);

    // Load contracts and company info to check access permissions
    useEffect(() => {
        const loadPermissions = async () => {
            if (!user) return;

            // Only load data if user is approved or verified
            if (user.status === UserStatus.APPROVED || user.status === UserStatus.VERIFIED) {
                try {
                    setContractsLoading(true);

                    // Load contracts to check if user has signed contract
                    const contracts = await contractService.getUserContracts();
                    const hasFullySignedContract = contracts.some(
                        (contract: Contract) => contract.status === 'SIGNED_BY_BOTH'
                    );
                    setHasSignedContract(hasFullySignedContract);

                    // Check if company is from 1C and has banking details
                    try {
                        const companyCheck = await api.checkCompanyFrom1C();
                        setCompanyFrom1C(companyCheck.data.from1C);
                        setHasBankingDetails(companyCheck.data.hasBankingDetails);
                    } catch (error) {
                        console.error('Ошибка проверки компании из 1С:', error);
                        setCompanyFrom1C(false);
                        setHasBankingDetails(false);
                    }

                    // Check if user is from 1C
                    setUserFrom1C(user.from1C || false);

                } catch (error) {
                    console.error('Ошибка загрузки данных:', error);
                    setHasSignedContract(false);
                    setCompanyFrom1C(false);
                    setHasBankingDetails(false);
                } finally {
                    setContractsLoading(false);
                }
            } else {
                setContractsLoading(false);
            }
        };

        loadPermissions();
    }, [user]);

    // Update userFrom1C when user data changes
    useEffect(() => {
        if (user) {
            console.log('🔄 ОБНОВЛЕНИЕ userFrom1C:', {
                userFrom1C: user.from1C,
                finalValue: user.from1C || false
            });
            setUserFrom1C(user.from1C || false);
        }
    }, [user]);

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setIsUserMenuOpen(false);
            }
        };

        if (isUserMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isUserMenuOpen]);

    if (!user) return null;

    // Определяем доступность вкладок на основе статуса пользователя и наличия подписанных договоров
    const isContractAccessible = user.status === UserStatus.APPROVED || user.status === UserStatus.VERIFIED;

    // Отладочная информация для диагностики
    console.log('🔍 ДИАГНОСТИКА ДОСТУПА К ЗАЯВКАМ:', {
        user: {
            status: user.status,
            from1C: user.from1C,
            login: user.login,
            fullName: user.fullName
        },
        conditions: {
            hasSignedContract,
            companyFrom1C,
            hasBankingDetails,
            userFrom1C
        },
        checks: {
            statusCheck: user.status === UserStatus.APPROVED || user.status === UserStatus.VERIFIED,
            signedContractCheck: hasSignedContract,
            company1CCheck: companyFrom1C && hasBankingDetails,
            user1CCheck: user.status === UserStatus.APPROVED && userFrom1C,
            finalCheck: hasSignedContract || (companyFrom1C && hasBankingDetails) || (user.status === UserStatus.APPROVED && userFrom1C)
        }
    });

    // Доступ к заявкам: пользователи из 1С или компании из 1С с банковскими реквизитами или компании с подписанными договорами
    // Специальное условие для пользователей из 1С: APPROVED + from1C=true дает доступ к заявкам
    const isOrdersAccessible = (user.status === UserStatus.APPROVED || user.status === UserStatus.VERIFIED) &&
        (hasSignedContract || (companyFrom1C && hasBankingDetails) || (user.status === UserStatus.APPROVED && userFrom1C));

    console.log('🎯 РЕЗУЛЬТАТ ДОСТУПА:', {
        isContractAccessible,
        isOrdersAccessible,
        reason: isOrdersAccessible ? 'Доступ разрешен' : 'Доступ запрещен'
    });

    const navigationItems: NavigationItem[] = [
        {
            id: 'dashboard',
            label: t('nav:dashboard'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2v0" />
                </svg>
            ),
            path: '/client/dashboard'
        },
        {
            id: 'products',
            label: t('nav:products'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
            ),
            path: '/client/products',
            disabled: !isOrdersAccessible
        },
        {
            id: 'orders',
            label: t('nav:orders'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
            ),
            path: '/client/orders',
            disabled: !isOrdersAccessible
        },
        {
            id: 'contract',
            label: t('nav:contract'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            ),
            path: '/client/contract',
            disabled: !isContractAccessible
        },
        {
            id: 'moderation',
            label: t('nav:moderation'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            path: '/client/moderation',
            badge: user.status === UserStatus.NEEDS_REVISION ? '!' : undefined
        },
        {
            id: 'documents',
            label: t('nav:documents'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            ),
            path: '/client/documents',
            disabled: !isContractAccessible
        },
        {
            id: 'profile',
            label: t('nav:profile'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            ),
            path: '/client/profile'
        },
    ];

    const getRoleDisplay = (role: UserRole) => {
        switch (role) {
            case UserRole.CLIENT:
                return t('common:roles.client');
            case UserRole.MODERATOR:
                return t('common:roles.moderator');
            case UserRole.SIGNATORY:
                return t('common:roles.signatory');
            case UserRole.SUPER_ADMIN:
                return t('common:roles.superAdmin');
            default:
                return role;
        }
    };

    const isActiveRoute = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const handleNavigation = (item: NavigationItem) => {
        if (!item.disabled) {
            navigate(item.path);
        }
    };

    return (
        <div className="h-screen bg-gray-50 flex overflow-hidden">
            {/* Sidebar */}
            <div className={`${isSidebarOpen ? 'w-64' : 'w-16'} bg-white shadow-lg border-r border-gray-200 transition-all duration-300 flex flex-col`}>
                {/* Header */}
                <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className={`flex items-center ${!isSidebarOpen && 'justify-center'}`}>
                            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center shadow-lg overflow-hidden p-1">
                                <Logo size="lg" className="w-full h-full rounded-lg" />
                            </div>
                            {isSidebarOpen && (
                                <div className="ml-3">
                                    <h1 className="text-lg font-bold text-gray-900">AISER</h1>
                                    <p className="text-xs text-gray-500">{t('common:clientPanel')}</p>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <svg className={`w-4 h-4 text-gray-500 transition-transform ${!isSidebarOpen && 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-2">
                    {navigationItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => handleNavigation(item)}
                            disabled={item.disabled}
                            className={`w-full flex items-center px-3 py-2.5 rounded-xl text-left transition-all duration-200 group ${isActiveRoute(item.path)
                                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm border border-blue-100'
                                : item.disabled
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                        >
                            <div className={`flex-shrink-0 ${isActiveRoute(item.path) ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}>
                                {item.icon}
                            </div>
                            {isSidebarOpen && (
                                <>
                                    <span className="ml-3 font-medium">{item.label}</span>
                                    {item.badge && !item.disabled && (
                                        <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${item.badge === '!'
                                            ? 'bg-orange-100 text-orange-600 animate-pulse'
                                            : 'bg-red-100 text-red-600'
                                            }`}>
                                            {item.badge}
                                        </span>
                                    )}
                                    {item.disabled && (
                                        <svg className="ml-auto w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    )}
                                </>
                            )}
                        </button>
                    ))}
                </nav>

                {/* User info */}
                <div className="p-4 border-t border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100">
                    <div className={`flex items-center ${!isSidebarOpen && 'justify-center'}`}>
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                            <span className="text-white font-semibold text-sm">
                                {user.fullName ? user.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
                            </span>
                        </div>
                        {isSidebarOpen && (
                            <div className="ml-3 flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{user.fullName || t('common:user')}</p>
                                <p className="text-xs text-gray-500">{getRoleDisplay(user.role)}</p>
                            </div>
                        )}
                    </div>

                    {/* Logout button - always visible */}
                    <button
                        onClick={logout}
                        className={`w-full mt-3 flex items-center px-3 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 ${!isSidebarOpen ? 'justify-center' : ''}`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {isSidebarOpen && <span className="ml-2">{t('common:logout')}</span>}
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col h-screen overflow-hidden">
                {/* Top bar */}
                <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <h2 className="text-xl font-semibold text-gray-900">
                                {navigationItems.find(item => isActiveRoute(item.path))?.label || t('common:clientPanel')}
                            </h2>
                        </div>
                        <div className="flex items-center space-x-4">
                            {/* Language Switcher */}
                            <LanguageSwitcher variant="compact" />

                            {/* User Profile Dropdown */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                    className="flex items-center space-x-3 hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors"
                                >
                                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm">
                                        <span className="text-white font-semibold text-xs">
                                            {user.fullName ? user.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-medium text-gray-900">{user.fullName || t('common:user')}</p>
                                        <p className="text-xs text-gray-500">{getRoleDisplay(user.role)}</p>
                                    </div>
                                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {/* Dropdown menu */}
                                {isUserMenuOpen && (
                                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                                        <button
                                            onClick={() => {
                                                navigate('/client/profile');
                                                setIsUserMenuOpen(false);
                                            }}
                                            className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            {t('common:profile')}
                                        </button>
                                        <button
                                            onClick={() => {
                                                logout();
                                                setIsUserMenuOpen(false);
                                            }}
                                            className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                        >
                                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                            </svg>
                                            {t('common:logout')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 p-6 overflow-y-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};
